import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, MessageCircle, Loader2 } from "lucide-react";
import { waLink } from "../../data/valuePacks";
import { fetchShopProduct } from "../../shop/api";
import { useCart } from "../../shop/CartContext";

/**
 * KitCard — shared Smart Home Kit card (Smart Value Pack / Luxury Smart Kit / Security Kit).
 * Handles the 2 BHK / 3 BHK variant toggle where a kit has more than one variant.
 * Used on both the homepage teaser and the dedicated /value-packs (Smart Home Kits) page.
 */
export default function KitCard({ kit, compact = false }) {
  const navigate = useNavigate();
  const cart = useCart();
  const [variantIdx, setVariantIdx] = useState(0);
  const [buying, setBuying] = useState(false);
  const variant = kit.variants[variantIdx];
  const coverage = compact ? variant.coverage.slice(0, 4) : variant.coverage;
  const waMessage = `Hi GenieX, I'm interested in the ${kit.name}${
    variant.label !== "All Homes" ? ` (${variant.label})` : ""
  } at ${variant.priceLabel}. Please share details and book a site visit.`;

  const handleBuyNow = async () => {
    if (!variant.slug) {
      toast.error("This kit isn't available for online purchase yet — please WhatsApp us.");
      return;
    }
    setBuying(true);
    try {
      const product = await fetchShopProduct(variant.slug);
      cart?.addItem(product, 1);
      toast.success(`${product.name} added to cart`);
      navigate("/checkout");
    } catch {
      toast.error("Couldn't fetch this kit right now. Please try again.");
    } finally {
      setBuying(false);
    }
  };

  return (
    <article
      data-testid={`kit-card-${kit.key}`}
      className={`card-lux relative flex flex-col ${compact ? "p-7 lg:p-8" : "p-8 lg:p-10"} ${
        kit.highlight ? "border-gold/40 shadow-[0_24px_60px_-20px_rgba(201,169,97,0.25)]" : ""
      }`}
    >
      {kit.highlight && (
        <span
          data-testid={`kit-badge-${kit.key}`}
          className="absolute -top-3 right-6 text-[10px] tracking-[0.28em] uppercase text-gold bg-[var(--gx-bg)] px-3 py-1 border border-gold/40 rounded-full"
        >
          Most Complete
        </span>
      )}

      <p className="text-[11px] tracking-[0.28em] uppercase text-mute">{kit.eyebrow}</p>
      <h3 className={`font-display text-white mt-3 tracking-tight ${compact ? "text-3xl lg:text-4xl" : "text-4xl lg:text-5xl"}`}>
        {kit.name}
      </h3>
      <p className="text-[14px] text-dim mt-2">{kit.tagline}</p>

      {kit.variants.length > 1 && (
        <div
          data-testid={`kit-bhk-toggle-${kit.key}`}
          className="mt-5 inline-flex rounded-full border border-line bg-white/[0.02] p-1 w-fit"
        >
          {kit.variants.map((v, i) => (
            <button
              key={v.key}
              type="button"
              data-testid={`kit-bhk-${kit.key}-${v.key}`}
              onClick={() => setVariantIdx(i)}
              className={`px-4 py-1.5 rounded-full text-[12.5px] tracking-wide transition-colors ${
                i === variantIdx ? "bg-gold text-black" : "text-dim hover:text-white"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-baseline gap-3">
        <span className="text-[18px] text-mute line-through">{variant.mrpLabel}</span>
        <span
          data-testid={`kit-price-${kit.key}`}
          className={`font-display text-gold tracking-tight ${compact ? "text-4xl lg:text-5xl" : "text-5xl lg:text-6xl"}`}
        >
          {variant.priceLabel}
        </span>
      </div>
      <p className="text-[12.5px] text-amber-200/80 mt-2">Installation &amp; 2-year warranty included</p>

      <p className="text-[13.5px] text-dim leading-relaxed mt-5">{kit.description}</p>

      <div className="mt-7 border-t border-line/70 pt-6">
        <p className="text-[11px] tracking-[0.28em] uppercase text-mute mb-4">What you get</p>
        <ul className="space-y-3">
          {coverage.map((c, i) => (
            <li key={`${c.room}-${i}`} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-gold mt-1 shrink-0" />
              <div>
                <p className="text-[13.5px] text-white tracking-tight">{c.room}</p>
                <p className="text-[12px] text-dim leading-relaxed mt-0.5">{c.items}</p>
              </div>
            </li>
          ))}
          {compact && variant.coverage.length > coverage.length && (
            <li className="text-[12px] text-mute pl-7">+{variant.coverage.length - coverage.length} more</li>
          )}
        </ul>
      </div>

      {kit.canExpand && (
        <p className="text-[11.5px] text-mute mt-6">Start here. Add more whenever you're ready.</p>
      )}

      <button
        type="button"
        onClick={handleBuyNow}
        disabled={buying}
        data-testid={`kit-buy-now-${kit.key}`}
        className={`mt-8 inline-flex items-center justify-center gap-2 ${kit.highlight ? "btn-gold" : "btn-outline"} disabled:opacity-60 disabled:cursor-wait`}
      >
        {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {buying ? "Adding…" : "Buy Now"}
      </button>
      <a
        href={waLink(waMessage)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`kit-whatsapp-${kit.key}`}
        className="mt-3 inline-flex items-center justify-center gap-2 text-[12.5px] text-mute hover:text-gold transition-colors"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        Have questions? Chat on WhatsApp
      </a>
    </article>
  );
}
